import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  Code, Smartphone, TrendingUp, Plane, Receipt, 
  Calendar, Car, Building2, Briefcase, MapPin, ArrowRight 
} from 'lucide-react';

const Services = () => {
  const { t } = useTranslation();

  const allServices = [
    {
      icon: <Code className="w-16 h-16 text-primary" />,
      title: 'Website Development',
      description: 'Custom business websites, e-commerce & booking sites for hotels, restaurants, tours, clubs and real estate in Pattaya',
      link: '/services/web-development',
      color: 'bg-blue-50'
    },
    {
      icon: <Smartphone className="w-16 h-16 text-primary" />,
      title: 'Mobile App Development',
      description: 'Android & iOS apps for travel booking, rental management, POS, nightlife, delivery and business operations',
      link: '/services/mobile-apps',
      color: 'bg-green-50'
    },
    {
      icon: <TrendingUp className="w-16 h-16 text-primary" />,
      title: 'Digital Marketing',
      description: 'SEO, Google Ads, social media marketing and annual maintenance contracts to rank #1 in Thailand',
      link: '/services/digital-marketing',
      color: 'bg-purple-50'
    },
    {
      icon: <Plane className="w-16 h-16 text-primary" />,
      title: 'Travel Portals (B2B & B2C)',
      description: 'Complete travel booking systems with hotel, flight, tour package management and GDS/API integration',
      link: '/services/travel-portals',
      color: 'bg-cyan-50'
    },
    {
      icon: <Receipt className="w-16 h-16 text-primary" />,
      title: 'POS Systems',
      description: 'Point of sale systems for restaurants, bars, nightclubs, retail stores with inventory and billing',
      link: '/services/pos-systems',
      color: 'bg-amber-50'
    },
    {
      icon: <Calendar className="w-16 h-16 text-primary" />,
      title: 'Booking Systems',
      description: 'Custom booking solutions for clubs, bars, yachts, shows, tours, spas, massage centers and events',
      link: '/services/booking-systems',
      color: 'bg-rose-50'
    },
    {
      icon: <Car className="w-16 h-16 text-primary" />,
      title: 'Rental Systems',
      description: 'Rental management software for cars, bikes, scooters, jetskis, boats and equipment in Pattaya',
      link: '/services/rental-systems',
      color: 'bg-indigo-50'
    },
    {
      icon: <Building2 className="w-16 h-16 text-primary" />,
      title: 'Real Estate Solutions',
      description: 'Property listing portals, buy/sell/rent classified systems, agent CRM and lead management',
      link: '/services/real-estate',
      color: 'bg-orange-50'
    },
    {
      icon: <Briefcase className="w-16 h-16 text-primary" />,
      title: 'Company Management Systems',
      description: 'HR, payroll, CRM, project management and complete business automation for Thailand companies',
      link: '/services/company-management',
      color: 'bg-teal-50'
    },
    {
      icon: <MapPin className="w-16 h-16 text-primary" />,
      title: 'Pattaya Local Business Solutions',
      description: 'Specialized systems for hotels, tours, clubs, bars, spas, massage centers, real estate and rentals',
      link: '/services/pattaya-solutions',
      color: 'bg-pink-50'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Services - Web Development, Apps & Digital Marketing in Pattaya</title>
        <meta name="description" content="Complete digital solutions for Pattaya businesses: website development, mobile apps, travel portals, POS systems, booking systems, rental software, real estate and more." />
        <meta name="keywords" content="Pattaya software development, Thailand web development, mobile app development, digital marketing Pattaya, travel portal, POS system" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6" data-aos="fade-up">
              Complete Digital Solutions Under One Roof
            </h1>
            <p className="text-xl md:text-2xl text-gold mb-8 max-w-3xl mx-auto" data-aos="fade-up" data-aos-delay="200">
              Professional Software Development for Pattaya & Thailand Businesses
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-aos="fade-up" data-aos-delay="400">
              <Link to="/contact" className="btn-gold">
                Get Free Consultation
              </Link>
              <a href="https://wa.me/66968826252" className="btn-primary" target="_blank" rel="noopener noreferrer">
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* All Services Grid */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4" data-aos="fade-up">
              Our Core Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-aos="fade-up" data-aos-delay="200">
              Click on any service to learn more about how we can help your business grow
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allServices.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className={`card ${service.color} hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer group`}
                data-aos="fade-up" 
                data-aos-delay={index * 50}
              >
                <div className="mb-6 group-hover:scale-110 transition-transform duration-300">{service.icon}</div>
                <h3 className="text-2xl font-bold text-primary mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <div className="text-primary font-semibold flex items-center group-hover:text-gold transition-colors">
                  View Details <ArrowRight className="ml-2 w-5 h-5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-gold">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-primary-700 mb-8">
            Let's discuss your requirements and build the perfect solution for your Pattaya business
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Request Free Quote
            </Link>
            <a href="tel:+66968826252" className="btn-outline">
              Call +66 96 882 6252
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
